'use client';

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { useLanguage } from '@/contexts/LanguageContext';
import { Language } from '@/types/i18n';
import { BackgroundParticles } from './BackgroundParticles';

// Professional welcome message component
const WelcomeMessage = ({ text, language, isExiting }: { text: string; language: Language; isExiting: boolean }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{
        opacity: isExiting ? 0 : 1,
        y: isExiting ? -15 : 0,
        scale: isExiting ? 0.95 : 1
      }}
      exit={{ opacity: 0, y: -30 }}
      transition={{
        duration: isExiting ? 1.0 : 0.5, // Slower exit, faster entrance
        ease: [0.25, 0.1, 0.25, 1]
      }}
      className="text-center"
    >
      <motion.h2
        className="text-4xl font-bold text-white mb-3 tracking-tight leading-tight"
        initial={{ opacity: 0 }}
        animate={{ opacity: isExiting ? 0 : 1 }}
        transition={{
          delay: isExiting ? 0 : 0.1, // Much faster entrance delay
          duration: isExiting ? 0.8 : 0.4, // Slower exit, faster entrance
          ease: [0.25, 0.1, 0.25, 1]
        }}
      >
        {text}
      </motion.h2>
      <motion.div
        className="w-16 h-0.5 bg-[#ac9cfc] mx-auto"
        initial={{ width: 0 }}
        animate={{
          width: isExiting ? 0 : 64,
          opacity: isExiting ? 0 : 1
        }}
        transition={{
          delay: isExiting ? 0 : 0.2, // Much faster entrance delay
          duration: isExiting ? 0.9 : 0.5, // Slower exit, faster entrance
          ease: [0.25, 0.1, 0.25, 1]
        }}
      />
    </motion.div>
  );
};

interface LanguageSelectionCardProps {
  isVisible: boolean;
  onLanguageSelect: (language: Language) => void;
}

export function LanguageSelectionCard({ isVisible, onLanguageSelect }: LanguageSelectionCardProps) {
  const { isLoading, isFirstVisit } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleLanguageSelect = (language: Language) => {
    if (isTransitioning || selectedLanguage) return;

    // For first-time visits, show welcome message but don't show progress bar
    if (isFirstVisit) {
      setSelectedLanguage(language);
      setIsTransitioning(true);

      // Phase 1: Card fades out gracefully (300ms)
      setTimeout(() => {
        setShowWelcome(true);
      }, 300);

      // Phase 2: Welcome message shows and stays (1200ms)
      setTimeout(() => {
        setIsExiting(true);
      }, 1500);

      // Phase 3: Welcome message fades out and load website (600ms)
      setTimeout(() => {
        onLanguageSelect(language);
      }, 2100); // Total: 2.1s for smooth, premium experience with welcome message
      return;
    }

    // For in-app language changes, use the existing transition process
    setSelectedLanguage(language);
    setIsTransitioning(true);

    // Phase 1: Card fades out gracefully (500ms)
    setTimeout(() => {
      setShowWelcome(true);
    }, 500);

    // Phase 2: Welcome message shows and stays (1400ms - perfect pause)
    setTimeout(() => {
      setIsExiting(true);
    }, 1900);

    // Phase 3: Welcome message fades out elegantly (700ms)
    setTimeout(() => {
      onLanguageSelect(language);
    }, 2600); // Total: 2.6s for smooth, premium experience
  };

  // Prevent all scrolling and interaction when card is visible
  useEffect(() => {
    if (isVisible) {
      // Prevent scrolling on body and html
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      // Prevent touch scrolling on mobile
      const preventTouchMove = (e: TouchEvent) => {
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      // Handle keyboard navigation
      const handleKeyDown = (event: KeyboardEvent) => {
        // Prevent all default keyboard actions except our language selection
        if (event.key === 'e' || event.key === 'E') {
          event.preventDefault();
          handleLanguageSelect('en');
        } else if (event.key === 'f' || event.key === 'F') {
          event.preventDefault();
          handleLanguageSelect('fr');
        } else {
          // Prevent other keyboard interactions
          event.preventDefault();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('touchmove', preventTouchMove);
        document.body.style.overflow = 'unset';
        document.documentElement.style.overflow = 'unset';
      };
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [isVisible, isTransitioning, selectedLanguage]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Background with particles */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 bg-background"
            onClick={(e) => e.stopPropagation()} // Prevent closing on backdrop click for first-time selection
          >
            {/* Subtle background particles */}
            <BackgroundParticles show={true} count={15} />

            {/* Subtle gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
          </motion.div>

          {/* Professional Welcome Message */}
          <AnimatePresence>
            {showWelcome && selectedLanguage && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{
                  duration: 0.4,
                  ease: [0.4, 0, 0.2, 1]
                }}
                className="absolute inset-0 flex items-center justify-center z-60"
              >
                <WelcomeMessage
                  text={selectedLanguage === 'en' ? 'Welcome!' : 'Bienvenue!'}
                  language={selectedLanguage}
                  isExiting={isExiting}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Language Selection Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: showWelcome ? 0 : (isTransitioning ? 0 : 1),
              y: showWelcome ? 10 : (isTransitioning ? 10 : 0)
            }}
            exit={{ opacity: 0, y: 20 }}
            transition={{
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1]
            }}
            className="relative w-full max-w-sm mx-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Language selection"
          >
            {/* Enhanced glass morphism card */}
            <div className="glass-card p-6 shadow-2xl relative overflow-hidden">
              {/* Multiple gradient overlays for depth */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/8 via-primary/5 to-transparent rounded-2xl" />
              <div className="absolute inset-0 bg-gradient-to-tl from-secondary/5 via-transparent to-white/3 rounded-2xl" />

              {/* Animated border glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 opacity-50 blur-sm animate-pulse" />

              {/* Content */}
              <div className="relative z-10 text-center space-y-6">
                {/* Logo with enhanced animations */}
                <motion.div
                  initial={{ opacity: 0, y: -20, scale: 0.8 }}
                  animate={{
                    opacity: isTransitioning ? 0 : 1,
                    y: isTransitioning ? -20 : 0,
                    scale: isTransitioning ? 0.8 : 1
                  }}
                  transition={{ delay: 0.3, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  className="flex justify-center"
                >
                  <motion.div
                    animate={{
                      filter: isTransitioning ? 'brightness(0.5)' : 'brightness(1)',
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <Image
                      src="/sicio-logo.png"
                      alt="Sicio"
                      width={120}
                      height={40}
                      className="drop-shadow-2xl"
                      priority
                    />
                  </motion.div>
                </motion.div>



                {/* Language Selection Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: isTransitioning ? 0 : 1, y: isTransitioning ? 20 : 0 }}
                  transition={{ delay: 0.5, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  className="flex gap-4"
                >
                  {/* English Button */}
                  <motion.button
                    onClick={() => handleLanguageSelect('en')}
                    disabled={isLoading || isTransitioning || selectedLanguage !== null}
                    whileHover={{
                      scale: selectedLanguage === null ? 1.02 : 1,
                      filter: selectedLanguage === null ? 'hue-rotate(15deg) brightness(1.1)' : 'none'
                    }}
                    whileTap={{ scale: selectedLanguage === null ? 0.98 : 1 }}
                    aria-label="Select English language"
                    className={`
                      flex-1 px-6 py-3 rounded-lg border transition-all duration-300
                      flex items-center justify-center
                      ${selectedLanguage === 'en'
                        ? 'bg-[#ac9cfc]/25 border-[#ac9cfc] text-white shadow-lg shadow-[#ac9cfc]/20'
                        : 'bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-white/30 hover:shadow-md'
                      }
                      ${isLoading || isTransitioning || selectedLanguage !== null ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                    `}
                  >
                    <span className="font-medium">English</span>
                  </motion.button>

                  {/* French Button */}
                  <motion.button
                    onClick={() => handleLanguageSelect('fr')}
                    disabled={isLoading || isTransitioning || selectedLanguage !== null}
                    whileHover={{
                      scale: selectedLanguage === null ? 1.02 : 1,
                      filter: selectedLanguage === null ? 'hue-rotate(15deg) brightness(1.1)' : 'none'
                    }}
                    whileTap={{ scale: selectedLanguage === null ? 0.98 : 1 }}
                    aria-label="Select French language"
                    className={`
                      flex-1 px-6 py-3 rounded-lg border transition-all duration-300
                      flex items-center justify-center
                      ${selectedLanguage === 'fr'
                        ? 'bg-[#ac9cfc]/25 border-[#ac9cfc] text-white shadow-lg shadow-[#ac9cfc]/20'
                        : 'bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-white/30 hover:shadow-md'
                      }
                      ${isLoading || isTransitioning || selectedLanguage !== null ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                    `}
                  >
                    <span className="font-medium">Français</span>
                  </motion.button>
                </motion.div>



              </div>

              {/* Enhanced decorative elements */}
              <div className="absolute -top-2 -left-2 w-12 h-12 bg-gradient-to-br from-primary/40 to-transparent rounded-full blur-md animate-pulse" />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-tl from-secondary/30 to-transparent rounded-full blur-sm animate-pulse" />
              <div className="absolute top-4 -right-4 w-6 h-6 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-sm animate-bounce" style={{ animationDelay: '1s' }} />
              <div className="absolute -bottom-4 left-4 w-4 h-4 bg-gradient-to-tl from-secondary/25 to-transparent rounded-full blur-sm animate-bounce" style={{ animationDelay: '2s' }} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default LanguageSelectionCard;